import React from 'react';
import {Card, Typography, IconButton} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { __ } from '../../Lang.js';
import {bcImageResize} from './functions.jsx';
import PersonalUseBadge from './PersonalUseBadge.jsx';

const GroupCard = ( {group, onDelete} ) => {
    const groupUrl = group.tag ? `/app/twi/results?category=${group.tag}` : group.permalinks.web;

    const handleDelete = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!confirm(__('Are you sure you want to delete this playlist? This cannot be undone.'))) {
            return;
        }

        try {
            const response = await fetch(`/api/v2/twi/mylist/${group.uniqid}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete playlist');
            }

            if (onDelete) {
                onDelete(group.uniqid);
            }
        } catch (error) {
            console.error('Error deleting playlist:', error);
            alert(__('Failed to delete playlist. Please try again.'));
        }
    };

    const baseImage = group.preview_image_url ?? 'https://now.cdn.msia.org/images/j-r/17.jpg';
    
    const imageUrl = bcImageResize(baseImage, '682x384');
    const imageSrcSet = baseImage 
        ? `${bcImageResize(baseImage, '435x245')} 435w, ${bcImageResize(baseImage, '682x384')} 682w, ${bcImageResize(baseImage, '377x212')} 377w`
        : '';
    const imageSizes = '(max-width: 500px) 435px, (min-width: 1200px) 377px, 682px';
    return (
          <div className="group-card">
              <a href={groupUrl} className="group-card__link video-card__link" tabIndex="0">
                  <div className="group-card__thumbnail-container">
                      <img src={imageUrl} 
                         alt={group.name} 
                        srcSet={imageSrcSet}
                        sizes={imageSizes}
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                        className="group-card__thumbnail"
                      />
                      <div className="group-card__info">
                          <Typography className="group-card__title">{group.name}</Typography>
                          <div className="group-card__badges">
                              <span className="group-card__badge">
                                  {group.is_audiobook ? __('Audio Book')
                                      : group.is_collection ? __('Collection')
                                      : group.is_custom ? __('My List')
                                      : __('Playlist')}
                              </span>
                              {Array.isArray(group.usage_type) && (() => {
                                  const types = group.usage_type;
                                  const nonRestrictive = ['public', 'personal', 'personal use'];
                                  const restrictiveTypes = types.filter(t => !nonRestrictive.includes(t.toLowerCase()));
                                  if (restrictiveTypes.length > 0) {
                                      return restrictiveTypes.map((t, i) => (
                                          <span key={i} className="group-card__badge video-card__badge--minister">{t}</span>
                                      ));
                                  }
                                  if (types.some(t => t.toLowerCase() === 'personal' || t.toLowerCase() === 'personal use')) {
                                      return <PersonalUseBadge badgeClass="group-card__badge video-card__badge--personal" />;
                                  }
                                  return null;
                              })()}
                              {group.visibility === 'SVOD Only' && (
                                  <span className="group-card__badge video-card__badge--premium" style={{ marginLeft: 'auto' }}>
                                      {__('Premium')}
                                  </span>
                              )}
                          </div>
                      </div>
                  </div>
              </a>
              {group.is_custom && (
                  <IconButton
                      onClick={handleDelete}
                      className="group-card__delete"
                      aria-label={__('Delete playlist')}
                      size="small"
                      sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                      }}
                  >
                      <DeleteIcon fontSize="small" />
                  </IconButton>
              )}
          </div>
    );
}

export default GroupCard;